import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { formatDistanceToNow } from 'date-fns'
import { AlertCircle, AlertTriangle, Award, Edit2, Loader2, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  useCreateMembershipBenefit,
  useDeleteMembershipBenefit,
  useUpdateMembershipBenefit,
} from '@/utils/api/membership-service'

import {
  AWARD_MODE_LABELS,
  toMembershipBenefitPayload,
  useBenefitCategories,
  useBenefitsConfig,
  type BenefitCategory,
  type BenefitAwardMode,
  type BenefitType,
} from './api/useBenefitsConfig'
import { useAwardBenefitMembers, useBenefitEligibleMembers } from './api/useBenefitAwards'

const benefitSchema = z.object({
  title: z.string().min(1, 'Title is required').max(120, 'Title must be 120 characters or less'),
  description: z.string().max(1000, 'Description must be 1000 characters or less'),
  categoryIds: z.array(z.string()).min(1, 'Select at least one category'),
  pointValue: z.coerce.number().int().min(0, 'Points cannot be negative').max(100, 'Points cannot exceed 100'),
  awardMode: z.enum(Object.keys(AWARD_MODE_LABELS) as [BenefitAwardMode, ...BenefitAwardMode[]]),
  isActive: z.boolean(),
})

type BenefitFormValues = z.infer<typeof benefitSchema>

const defaultBenefitValues: BenefitFormValues = {
  title: '',
  description: '',
  categoryIds: [],
  pointValue: 10,
  awardMode: 'MANUAL_SELECTION',
  isActive: true,
}

const workbookScoreGuide = [
  { applicability: 'General / Professional', examples: 'Mentorship, CPD, conferences, AGM', points: '5-20 pts' },
  { applicability: 'Student', examples: 'Academic/professional program, CPD, networking, AGM', points: '10-40 pts' },
  { applicability: 'Affiliate', examples: 'Mentorship, CPD, networking, AGM, book club', points: '10-40 pts' },
  {
    applicability: 'Professional / Associate / Member',
    examples: 'Chartered Banker, CPD, networking, AGM, ambassador',
    points: '10-40 pts',
  },
  { applicability: 'Honorary Member', examples: 'AGM, thought leadership, networking, mentoring', points: '10-40 pts' },
  { applicability: 'Fellow / Honorary Fellow', examples: 'Mentor, speaker, networking, board contribution', points: '10-30 pts' },
]

const membershipThresholds = [
  { label: 'Bronze', points: 35 },
  { label: 'Silver', points: 65 },
  { label: 'Gold', points: 75 },
  { label: 'Platinum', points: 85 },
]

function formatRelativeDate(value: string) {
  const parsed = Date.parse(value)
  if (!Number.isFinite(parsed)) return 'Not updated'
  return formatDistanceToNow(new Date(parsed), { addSuffix: true })
}

function BenefitForm({
  categories,
  initialValues,
  isLoading,
  onSubmit,
}: {
  categories: BenefitCategory[]
  initialValues?: BenefitFormValues
  isLoading: boolean
  onSubmit: (values: BenefitFormValues) => Promise<void>
}) {
  const form = useForm<BenefitFormValues>({
    resolver: zodResolver(benefitSchema),
    defaultValues: initialValues ?? defaultBenefitValues,
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Benefit</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Thought leadership platform" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="How this benefit contributes to a member score"
                  className="min-h-24 resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          control={form.control}
          name="categoryIds"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Categories</FormLabel>
              <div className="max-h-48 space-y-2 overflow-y-auto rounded-md border p-3">
                {categories.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No categories available.</div>
                ) : (
                  categories.map((category) => {
                    const checked = field.value.includes(category.id)
                    return (
                      <label key={category.id} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(value) => {
                            const next = Boolean(value)
                              ? [...field.value, category.id]
                              : field.value.filter((id) => id !== category.id)
                            field.onChange(Array.from(new Set(next)))
                          }}
                        />
                        {category.name}
                      </label>
                    )
                  })
                )}
              </div>
              <FormDescription>Select one or more categories that should receive this benefit.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

          <FormField
            control={form.control}
            name="pointValue"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Score Value</FormLabel>
                <FormControl>
                  <Input type="number" min="0" max="100" {...field} />
                </FormControl>
                <FormDescription>Points awarded to selected or automatically qualifying members.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <FormField
            control={form.control}
            name="awardMode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Award Mode</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.entries(AWARD_MODE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>Use manual selection for technical benefits that need admin review.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="isActive"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-md border px-3 py-2">
                <div>
                  <FormLabel>Active</FormLabel>
                  <FormDescription>Inactive benefits stay configured but do not count for scoring.</FormDescription>
                </div>
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <Button type="submit" disabled={isLoading} className="w-full">
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Benefit'
          )}
        </Button>
      </form>
    </Form>
  )
}

function CreateBenefitDialog({ categories, onSuccess }: { categories: BenefitCategory[]; onSuccess: () => void }) {
  const [open, setOpen] = useState(false)
  const createMutation = useCreateMembershipBenefit()

  const handleSubmit = async (values: BenefitFormValues) => {
    try {
      await createMutation.mutateAsync({ data: toMembershipBenefitPayload(values) })
      toast.success('Benefit created successfully')
      setOpen(false)
      onSuccess()
    } catch {
      toast.error('Failed to create benefit')
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          Add Benefit
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Benefit</DialogTitle>
          <DialogDescription>Configure a benefit and how members earn points from it.</DialogDescription>
        </DialogHeader>
        <BenefitForm categories={categories} isLoading={createMutation.isPending} onSubmit={handleSubmit} />
      </DialogContent>
    </Dialog>
  )
}

function EditBenefitDialog({
  benefit,
  categories,
  onSuccess,
}: {
  benefit: BenefitType
  categories: BenefitCategory[]
  onSuccess: () => void
}) {
  const [open, setOpen] = useState(false)
  const updateMutation = useUpdateMembershipBenefit()

  const handleSubmit = async (values: BenefitFormValues) => {
    const benefitId = Number(benefit.id)
    if (!Number.isFinite(benefitId)) {
      toast.error('Cannot update benefit: invalid id')
      return
    }

    try {
      await updateMutation.mutateAsync({ id: benefitId, data: toMembershipBenefitPayload(values) })
      toast.success('Benefit updated successfully')
      setOpen(false)
      onSuccess()
    } catch {
      toast.error('Failed to update benefit')
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Edit2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Benefit</DialogTitle>
          <DialogDescription>Update the score configuration for this benefit.</DialogDescription>
        </DialogHeader>
        <BenefitForm
          initialValues={{
            title: benefit.title,
            description: benefit.description,
            categoryIds: benefit.categoryIds,
            pointValue: benefit.pointValue,
            awardMode: benefit.awardMode,
            isActive: benefit.isActive,
          }}
          categories={categories}
          isLoading={updateMutation.isPending}
          onSubmit={handleSubmit}
        />
      </DialogContent>
    </Dialog>
  )
}

function AwardPointsDialog({ benefit }: { benefit: BenefitType }) {
  const [open, setOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const membersQuery = useBenefitEligibleMembers(benefit.id, open)
  const awardMutation = useAwardBenefitMembers(benefit.id)

const members = membersQuery.data ?? []
const categoryLabel = benefit.categories.length > 0 ? benefit.categories.map((category) => category.name).join(', ') : 'selected categories'
  const selectableMembers = useMemo(
    () => members.filter((member) => !member.alreadyAwarded),
    [members],
  )

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set())
    }
  }, [open])

  const allSelectableSelected =
    selectableMembers.length > 0 && selectableMembers.every((member) => selectedIds.has(member.applicationId))

  const toggleMember = (applicationId: number, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current)
      if (checked) {
        next.add(applicationId)
      } else {
        next.delete(applicationId)
      }
      return next
    })
  }

  const toggleAll = (checked: boolean) => {
    if (!checked) {
      setSelectedIds(new Set())
      return
    }
    setSelectedIds(new Set(selectableMembers.map((member) => member.applicationId)))
  }

  const handleAward = async () => {
    const applicationIds = Array.from(selectedIds)
    if (applicationIds.length === 0) {
      toast.error('Select at least one member to award')
      return
    }

    try {
      await awardMutation.mutateAsync(applicationIds)
      toast.success(`Awarded ${benefit.pointValue} points to ${applicationIds.length} member(s)`)
      setSelectedIds(new Set())
      await membersQuery.refetch()
    } catch {
      toast.error('Failed to award benefit points')
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={!benefit.isActive}>
          <Award className="mr-2 h-4 w-4" />
          Award Points
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Award {benefit.title}</DialogTitle>
          <DialogDescription>
            Select qualifying {categoryLabel} members to receive{' '}
            {benefit.pointValue} points.
          </DialogDescription>
        </DialogHeader>

        {membersQuery.isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, index) => (
              <Skeleton key={index} className="h-12 w-full" />
            ))}
          </div>
        ) : membersQuery.isError ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Could not load eligible members</AlertTitle>
            <AlertDescription>Try closing and opening the dialog again.</AlertDescription>
          </Alert>
        ) : members.length === 0 ? (
          <div className="rounded-md border p-6 text-center text-sm text-muted-foreground">
            No approved or renewed members found for these categories.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-md border px-3 py-2">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={allSelectableSelected}
                  onCheckedChange={(value) => toggleAll(Boolean(value))}
                  disabled={selectableMembers.length === 0}
                />
                <div>
                  <div className="text-sm font-medium">Select all not yet awarded</div>
                  <div className="text-xs text-muted-foreground">
                    {selectableMembers.length} available, {members.length - selectableMembers.length} already awarded
                  </div>
                </div>
              </div>
              <Badge variant="outline">{selectedIds.size} selected</Badge>
            </div>

            <div className="max-h-[420px] overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-background">
                  <tr className="border-b text-left">
                    <th className="w-12 px-4 py-3"></th>
                    <th className="px-4 py-3 font-medium">Member</th>
                    <th className="px-4 py-3 font-medium">Contact</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {members.map((member) => (
                    <tr key={member.applicationId} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <Checkbox
                          checked={selectedIds.has(member.applicationId)}
                          disabled={member.alreadyAwarded}
                          onCheckedChange={(value) => toggleMember(member.applicationId, Boolean(value))}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{member.memberName}</div>
                        <div className="text-xs text-muted-foreground">{member.memberId}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div>{member.email ?? 'No email'}</div>
                        <div className="text-xs text-muted-foreground">{member.phone ?? 'No phone'}</div>
                      </td>
                      <td className="px-4 py-3">
                        {member.alreadyAwarded ? (
                          <Badge variant="secondary">Awarded {member.awardedPoints} pts</Badge>
                        ) : (
                          <Badge variant="outline">Not awarded</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>
                Close
              </Button>
              <Button onClick={handleAward} disabled={awardMutation.isPending || selectedIds.size === 0}>
                {awardMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Awarding...
                  </>
                ) : (
                  `Award ${selectedIds.size} member(s)`
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function DeleteBenefitDialog({ benefit, onSuccess }: { benefit: BenefitType; onSuccess: () => void }) {
  const [open, setOpen] = useState(false)
  const deleteMutation = useDeleteMembershipBenefit()

  const handleDelete = async () => {
    const benefitId = Number(benefit.id)
    if (!Number.isFinite(benefitId)) {
      toast.error('Cannot delete benefit: invalid id')
      return
    }

    try {
      await deleteMutation.mutateAsync({ id: benefitId })
      toast.success('Benefit deleted successfully')
      setOpen(false)
      onSuccess()
    } catch {
      toast.error('Failed to delete benefit')
    }
  }

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Trash2 className="h-4 w-4" />
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete benefit?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes {benefit.title} from benefit scoring configuration.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleteMutation.isPending}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function BenefitsTable({
  benefits,
  categories,
  onRefresh,
}: {
  benefits: BenefitType[]
  categories: BenefitCategory[]
  onRefresh: () => void
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left">
            <th className="px-4 py-3 font-medium">Benefit</th>
            <th className="px-4 py-3 font-medium">Categories</th>
            <th className="px-4 py-3 font-medium">Award Mode</th>
            <th className="px-4 py-3 text-right font-medium">Score</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium">Updated</th>
            <th className="px-4 py-3 text-right font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {benefits.map((benefit) => (
            <tr key={benefit.id} className="transition hover:bg-muted/30">
              <td className="px-4 py-3">
                <div className="font-medium">{benefit.title}</div>
                {benefit.description ? (
                  <div className="max-w-md truncate text-xs text-muted-foreground">{benefit.description}</div>
                ) : null}
              </td>
              <td className="px-4 py-3">
                <div className="flex max-w-xs flex-wrap gap-1">
                  {benefit.categories.length > 0 ? benefit.categories.map((category) => (
                    <Badge key={category.id} variant="outline">{category.name}</Badge>
                  )) : <Badge variant="outline">No categories</Badge>}
                </div>
              </td>
              <td className="px-4 py-3">
                <Badge variant={benefit.awardMode === 'AUTOMATIC' ? 'default' : 'secondary'}>
                  {AWARD_MODE_LABELS[benefit.awardMode]}
                </Badge>
              </td>
              <td className="px-4 py-3 text-right font-semibold">{benefit.pointValue}</td>
              <td className="px-4 py-3">
                {benefit.isActive ? (
                  <Badge className="bg-green-500/10 text-green-700 hover:bg-green-500/10">Active</Badge>
                ) : (
                  <Badge variant="secondary">Inactive</Badge>
                )}
              </td>
              <td className="px-4 py-3 text-xs text-muted-foreground">
                {formatRelativeDate(benefit.updatedAt)}
              </td>
              <td className="px-4 py-3">
                <div className="flex justify-end gap-2">
                  {benefit.awardMode === 'MANUAL_SELECTION' ? <AwardPointsDialog benefit={benefit} /> : null}
                  <EditBenefitDialog benefit={benefit} categories={categories} onSuccess={onRefresh} />
                  <DeleteBenefitDialog benefit={benefit} onSuccess={onRefresh} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function BenefitsConfigPage() {
  const benefitsQuery = useBenefitsConfig()
  const categoriesQuery = useBenefitCategories()
  const categories = categoriesQuery.data ?? []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Benefits Configuration</h1>
        <p className="mt-2 text-muted-foreground">
          Manage CPD benefit scores by category and award mode.
        </p>
      </div>

      <Alert className="border-blue-200 bg-blue-50 text-blue-900">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Manual and automatic benefit awards</AlertTitle>
        <AlertDescription>
          Automatic benefits are granted to all qualifying members. Manual selection benefits load qualifying
          members in a dialog so an admin can choose who should receive the points.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Configured Benefits</CardTitle>
            <CardDescription>{benefitsQuery.data?.length ?? 0} benefit records configured</CardDescription>
          </div>
          {!benefitsQuery.isLoading && !categoriesQuery.isLoading && (
              <CreateBenefitDialog categories={categories} onSuccess={benefitsQuery.refetch} />
            )}
        </CardHeader>
        <CardContent>
          {benefitsQuery.isLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, index) => (
                <Skeleton key={index} className="h-16 w-full" />
              ))}
            </div>
          ) : benefitsQuery.isError ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Error loading benefits</AlertTitle>
              <AlertDescription>Failed to load benefit configuration. Please try again.</AlertDescription>
            </Alert>
          ) : benefitsQuery.data?.length === 0 ? (
            <div className="py-12 text-center">
              <p className="mb-4 text-muted-foreground">No benefits configured yet</p>
              <CreateBenefitDialog categories={categories} onSuccess={benefitsQuery.refetch} />
            </div>
          ) : (
            <BenefitsTable benefits={benefitsQuery.data ?? []} categories={categories} onRefresh={() => benefitsQuery.refetch()} />
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <Card>
          <CardHeader>
            <CardTitle>Workbook Score Guide</CardTitle>
            <CardDescription>Reference scoring bands from Membership CPD Score.xlsx.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {workbookScoreGuide.map((item) => (
                <div key={item.applicability} className="rounded-md border p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium">{item.applicability}</div>
                      <div className="text-sm text-muted-foreground">{item.examples}</div>
                    </div>
                    <Badge variant="outline">{item.points}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Member Levels</CardTitle>
            <CardDescription>Score thresholds used by the CPD sheet.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {membershipThresholds.map((threshold) => (
                <div key={threshold.label} className="flex items-center justify-between rounded-md border p-3">
                  <span className="font-medium">{threshold.label}</span>
                  <Badge>{threshold.points} pts</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
